import { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  TrendingUp, Music2, Video, Search, ExternalLink, RefreshCw,
  AlertTriangle, Flame, Newspaper, Users, ListMusic, Mail, Instagram,
  ShieldAlert, ShieldCheck, Loader2, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { timeAgo } from "@/lib/format";

const SPOTIFY_PATH = { artist: "artist", track: "track", playlist: "playlist", user: "user" };
const fmtNum = (n) => (n == null ? "—" : Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n));

function EntityIcon({ type }) {
  const Icon = type === "artist" ? Users : type === "playlist" ? ListMusic : type === "user" ? Users : Music2;
  return <Icon className="h-4 w-4" />;
}

function TopMatch({ item }) {
  const href = item.spotifyId ? `https://open.spotify.com/${SPOTIFY_PATH[item.entityType] || "search"}/${item.spotifyId}` : "#";
  return (
    <a
      data-testid={`viral-top-${item.entityType}`}
      href={href} target="_blank" rel="noopener noreferrer"
      className="group flex items-center gap-3 rounded-xl border border-black/[0.06] bg-white p-3 transition-colors hover:border-[#35606E]/40"
    >
      {item.coverArtUrl ? (
        <img src={item.coverArtUrl} alt="" className={`h-12 w-12 shrink-0 object-cover ${item.entityType === "artist" || item.entityType === "user" ? "rounded-full" : "rounded-lg"}`} />
      ) : (
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-[#35606E]/10 text-[#35606E]"><EntityIcon type={item.entityType} /></span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold leading-tight">{item.name}</p>
        <p className="flex items-center gap-1 text-xs capitalize text-neutral-500"><EntityIcon type={item.entityType} /> {item.entityType}{item.artistData?.length ? ` · ${item.artistData.map((a) => a.name).join(", ")}` : ""}</p>
      </div>
      <ExternalLink className="h-4 w-4 shrink-0 text-neutral-300 transition-colors group-hover:text-[#35606E]" />
    </a>
  );
}

function PlaylistCard({ p }) {
  return (
    <div data-testid="viral-playlist-card" className="pb-card flex flex-col gap-3 p-4">
      <div className="flex items-start gap-3">
        {p.coverArtUrl ? (
          <img src={p.coverArtUrl} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
        ) : (
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-neutral-900 text-neutral-500"><ListMusic className="h-5 w-5" /></span>
        )}
        <div className="min-w-0 flex-1">
          <a href={p.url || "#"} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-semibold leading-tight hover:text-[#35606E]">
            <span className="truncate">{p.name}</span>
            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-neutral-300" />
          </a>
          {p.owner && <p className="truncate text-xs text-neutral-500">by {p.owner}</p>}
          {p.genres?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {p.genres.map((g) => <span key={g} className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] capitalize text-neutral-500">{g}</span>)}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 border-t border-black/[0.06] pt-3">
        <div>
          <p className="num-display text-lg font-extrabold leading-none">{fmtNum(p.followers)}</p>
          <p className="text-[11px] text-neutral-500">followers</p>
        </div>
        <div>
          <p className="num-display text-lg font-extrabold leading-none text-[#35606E]">{p.avgDailyFollowers != null ? `+${fmtNum(Math.round(p.avgDailyFollowers))}` : "—"}</p>
          <p className="text-[11px] text-neutral-500">avg / day (14d)</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {p.botted ? (
          <span className="flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700"><ShieldAlert className="h-3 w-3" /> Botted risk</span>
        ) : (
          <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"><ShieldCheck className="h-3 w-3" /> Looks legit</span>
        )}
        {p.contacts?.email && (
          <a href={`mailto:${p.contacts.email}`} className="flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-[11px] text-neutral-600 hover:border-[#35606E]/40 hover:text-[#35606E]"><Mail className="h-3 w-3" /> {p.contacts.email}</a>
        )}
        {p.contacts?.instagram && (
          <a href={`https://instagram.com/${p.contacts.instagram.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-[11px] text-neutral-600 hover:border-[#35606E]/40 hover:text-[#35606E]"><Instagram className="h-3 w-3" /> {p.contacts.instagram}</a>
        )}
      </div>
    </div>
  );
}

function ViralDiscovery() {
  const [chips, setChips] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => {
    api.get("/discovery/chips").then((r) => setChips(r.data.chips || [])).catch(() => {});
  }, []);

  const run = useCallback(async (query) => {
    const term = (query ?? q).trim();
    if (!term) return;
    setQ(term);
    setLoading(true);
    try {
      const { data } = await api.get(`/discovery/viral?q=${encodeURIComponent(term)}`);
      setRes(data);
    } catch {
      toast.error("Couldn't search artist.tools right now");
    } finally {
      setLoading(false);
    }
  }, [q]);

  const playlists = res?.playlists || [];
  const top = res?.top || [];

  return (
    <section className="mb-14" data-testid="viral-discovery">
      <div className="mb-4 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-[#35606E]" />
        <h2 className="font-display text-xl font-bold">Viral artist &amp; playlist discovery</h2>
      </div>
      <p className="mb-4 max-w-2xl text-sm text-neutral-500">
        Search Spotify artists, tracks and fast-growing playlists via artist.tools — with real follower growth,
        curator contacts and bot detection. Great for finding placement targets for a rollout.
      </p>

      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input
            data-testid="viral-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search an artist, song or vibe (e.g. Brent Faiyaz, rnb)…"
            className="rounded-full pl-9"
          />
        </div>
        <Button data-testid="viral-search-btn" type="submit" disabled={loading} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
        </Button>
      </form>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {chips.map((c) => (
            <button
              key={c}
              data-testid="viral-chip"
              onClick={() => run(c)}
              className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-medium text-neutral-600 transition-colors hover:border-[#35606E]/40 hover:text-[#35606E]"
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}
        </div>
      )}

      {!loading && res && (
        <div className="mt-6 space-y-8">
          {res.top_status === "error" || res.playlists_status === "error" ? (
            <div data-testid="viral-error" className="pb-card flex flex-col items-center gap-2 p-8 text-center">
              <AlertTriangle className="h-7 w-7 text-neutral-300" />
              <p className="text-sm font-medium text-neutral-600">artist.tools is unavailable right now</p>
              <p className="text-xs text-neutral-500">{res.error || "Try again in a moment — we never show placeholder data."}</p>
            </div>
          ) : null}

          {top.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-400">Top matches</h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {top.map((it, i) => <TopMatch key={`${it.entityType}-${it.spotifyId}-${i}`} item={it} />)}
              </div>
            </div>
          )}

          {playlists.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-400">Fast-growing playlists</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {playlists.map((p) => <PlaylistCard key={p.spotifyId} p={p} />)}
              </div>
            </div>
          )}

          {top.length === 0 && playlists.length === 0 && res.top_status === "ok" && res.playlists_status === "ok" && (
            <div className="pb-card px-4 py-10 text-center text-sm text-neutral-500">No matches for “{res.query}”.</div>
          )}
        </div>
      )}

      {!loading && !res && (
        <div className="mt-6 pb-card flex flex-col items-center gap-2 p-10 text-center">
          <ListMusic className="h-8 w-8 text-neutral-300" />
          <p className="text-sm font-medium text-neutral-600">Search to discover placement targets</p>
          <p className="text-xs text-neutral-500">Tap a chip above or type an artist / vibe to begin.</p>
        </div>
      )}
    </section>
  );
}

function CreatorTrendsCard({ seed }) {
  const [q, setQ] = useState(seed || "");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const run = useCallback(async (term) => {
    const t = (term ?? q).trim();
    if (!t) return;
    setQ(t);
    setLoading(true);
    try {
      const { data } = await api.get(`/discovery/creator-trends?q=${encodeURIComponent(t)}`);
      setRes(data);
    } catch {
      setRes({ status: "unavailable", videos: [], creators: [] });
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { if (seed) run(seed); /* auto-run once with a seed */ }, [seed]);

  const hasData = res && res.status === "ok" && (res.videos?.length || res.creators?.length);

  return (
    <section className="mb-14" data-testid="creator-trends">
      <div className="mb-4 flex items-center gap-2">
        <Flame className="h-5 w-5 text-[#35606E]" />
        <h2 className="font-display text-xl font-bold">Creator trends · TikTok</h2>
      </div>
      <p className="mb-4 max-w-2xl text-sm text-neutral-500">
        Real trending TikTok videos and creators for a keyword, via EnsembleData. Use it to spot
        sounds and creators gaining traction. Real data only.
      </p>

      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="mb-5 flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input data-testid="creator-trends-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keyword, sound or artist (e.g. Brent Faiyaz)…" className="rounded-full pl-9" />
        </div>
        <Button data-testid="creator-trends-btn" type="submit" disabled={loading} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
        </Button>
      </form>

      {loading && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
        </div>
      )}

      {!loading && hasData && (
        <div className="space-y-8">
          {res.creators?.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-400">Creators to watch</h3>
              <div className="flex flex-wrap gap-2">
                {res.creators.map((c) => (
                  <a key={c.handle} data-testid="creator-trend-creator" href={c.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-sm hover:border-[#35606E]/40">
                    <Video className="h-3.5 w-3.5 text-[#35606E]" />
                    <span className="font-medium">@{c.handle}</span>
                    {c.followers != null && <span className="text-xs text-neutral-400">{fmtNum(c.followers)} followers</span>}
                  </a>
                ))}
              </div>
            </div>
          )}
          {res.videos?.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-400">Trending videos</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {res.videos.map((v) => (
                  <a key={v.id} data-testid="creator-trend-video" href={v.url || "#"} target="_blank" rel="noopener noreferrer" className="pb-card flex flex-col gap-2 p-4 hover:border-[#35606E]/40">
                    <p className="line-clamp-2 text-sm font-medium">{v.caption || `@${v.author_handle}`}</p>
                    <p className="text-xs text-neutral-500">@{v.author_handle}</p>
                    <div className="mt-1 flex items-center gap-4 text-xs text-neutral-500">
                      <span className="num-display font-semibold text-[#111]">{fmtNum(v.views)} views</span>
                      <span>{fmtNum(v.likes)} likes</span>
                      <span>{fmtNum(v.comments)} comments</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && res && !hasData && (
        <div className="pb-card flex flex-col items-center gap-2 p-10 text-center">
          <AlertTriangle className={`h-7 w-7 ${res.status === "quota" ? "text-amber-400" : "text-neutral-300"}`} />
          <p className="text-sm font-medium text-neutral-600">
            {res.status === "quota" ? "Waiting on EnsembleData quota" : res.status === "no_key" ? "EnsembleData not configured" : `No trends found for “${res.query}”`}
          </p>
          <p className="max-w-md text-xs text-neutral-500">
            {res.status === "quota"
              ? "Real TikTok trends populate here automatically once the EnsembleData quota resets — we never show placeholder numbers."
              : res.status === "unavailable" ? "EnsembleData is unavailable right now — try again shortly."
              : "Try a different keyword."}
          </p>
        </div>
      )}

      {!loading && !res && (
        <div className="pb-card flex flex-col items-center gap-2 p-10 text-center">
          <Flame className="h-8 w-8 text-neutral-300" />
          <p className="text-sm font-medium text-neutral-600">Search a keyword to see trending TikTok creators</p>
        </div>
      )}
    </section>
  );
}

const KIND_ICON = { topic: Search, song: Music2, video: Video };

function StatusPill({ status }) {
  const map = {
    ok: ["Live", "bg-[#D1FAE5] text-[#065F46]"],
    stale: ["Cached", "bg-[#FEF3C7] text-[#92400E]"],
    error: ["Unavailable", "bg-[#FEE2E2] text-[#991B1B]"],
    loading: ["Loading", "bg-neutral-100 text-neutral-500"],
  };
  const [label, cls] = map[status] || map.loading;
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>{label}</span>;
}

function TopicItem({ item, index }) {
  return (
    <a
      data-testid="discovery-topic-item"
      href={item.news_url || item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-neutral-50"
    >
      <span className="num-display w-6 shrink-0 text-sm font-bold text-neutral-300">{index + 1}</span>
      {item.image ? (
        <img src={item.image} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
      ) : (
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#35606E]/10 text-[#35606E]"><Flame className="h-4 w-4" /></span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold capitalize leading-tight">{item.title}</p>
        {item.news_title && (
          <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-neutral-500">
            <Newspaper className="h-3 w-3 shrink-0" /> {item.news_title}
          </p>
        )}
      </div>
      {item.traffic && (
        <span className="shrink-0 rounded-full bg-[#35606E]/10 px-2 py-1 text-xs font-semibold text-[#35606E]">{item.traffic}</span>
      )}
      <ExternalLink className="h-4 w-4 shrink-0 text-neutral-300 transition-colors group-hover:text-[#35606E]" />
    </a>
  );
}

function TrackItem({ item, index }) {
  return (
    <a
      data-testid="discovery-track-item"
      href={item.url || "#"}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-neutral-50"
    >
      <span className="num-display w-6 shrink-0 text-sm font-bold text-neutral-300">{item.rank || index + 1}</span>
      {item.image ? (
        <img src={item.image} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover shadow-sm" />
      ) : (
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-neutral-900 text-neutral-500"><Music2 className="h-4 w-4" /></span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold leading-tight">{item.title}</p>
        <p className="truncate text-xs text-neutral-500">{item.subtitle}{item.views ? ` · ${item.views}` : ""}</p>
      </div>
      {item.genre && <span className="hidden shrink-0 rounded-full bg-neutral-100 px-2 py-1 text-[11px] text-neutral-500 sm:block">{item.genre}</span>}
      <ExternalLink className="h-4 w-4 shrink-0 text-neutral-300 transition-colors group-hover:text-[#35606E]" />
    </a>
  );
}

function SourceCard({ source, index }) {
  const Icon = KIND_ICON[source.kind] || TrendingUp;
  const isLoading = source.status === "loading";
  const isError = source.status === "error";
  const items = source.items || [];

  return (
    <motion.div
      data-testid={`discovery-source-${source.id}`}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="pb-card flex flex-col overflow-hidden p-0"
    >
      <div className="flex items-start justify-between gap-3 border-b border-black/[0.06] p-4">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#35606E]/10 text-[#35606E]"><Icon className="h-4 w-4" /></span>
          <div>
            <a href={source.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-display text-base font-bold leading-tight hover:text-[#35606E]">
              {source.label} <ExternalLink className="h-3 w-3 text-neutral-300" />
            </a>
            <p className="text-xs text-neutral-500">
              {source.fetched_at ? `Fetched ${timeAgo(source.fetched_at)}` : "Not fetched yet"}
            </p>
          </div>
        </div>
        <StatusPill status={source.status} />
      </div>

      <div className="flex-1 p-2">
        {source.status === "stale" && (
          <p className="m-2 flex items-start gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Couldn't refresh — showing the last fetched results.
          </p>
        )}
        {isLoading ? (
          <div className="space-y-2 p-1">
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}
          </div>
        ) : isError ? (
          <div data-testid={`discovery-error-${source.id}`} className="flex flex-col items-center gap-2 px-4 py-10 text-center">
            <AlertTriangle className="h-7 w-7 text-neutral-300" />
            <p className="text-sm font-medium text-neutral-600">Not available right now</p>
            <p className="max-w-xs text-xs text-neutral-500">{source.error}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-neutral-500">No results returned.</div>
        ) : (
          <div className="divide-y divide-black/[0.04]">
            {items.map((it, i) =>
              source.kind === "topic"
                ? <TopicItem key={i} item={it} index={i} />
                : <TrackItem key={i} item={it} index={i} />
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function Discovery() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/discovery");
      setData(data);
      return data;
    } catch {
      toast.error("Failed to load discovery");
      return null;
    }
  }, []);

  useEffect(() => {
    let tries = 0;
    const tick = async () => {
      const d = await load();
      const anyLoading = d?.sources?.some((s) => s.status === "loading");
      if (anyLoading && tries < 6) {
        tries += 1;
        pollRef.current = setTimeout(tick, 4000);
      }
    };
    tick();
    return () => clearTimeout(pollRef.current);
  }, [load]);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/discovery/refresh");
      toast.success("Refreshing trend sources…");
      let tries = 0;
      const poll = async () => {
        const d = await load();
        tries += 1;
        if (tries < 6) setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch {
      toast.error("Couldn't trigger a refresh");
    } finally {
      setTimeout(() => setRefreshing(false), 3500);
    }
  };

  const sources = data?.sources || [];
  const searchSources = sources.filter((s) => s.group === "searches");
  const musicSources = sources.filter((s) => s.group === "music");

  return (
    <div className="min-h-screen">
      <TopBar
        right={
          <Button
            data-testid="discovery-refresh-btn"
            onClick={doRefresh}
            disabled={refreshing}
            variant="outline"
            className="rounded-full border-neutral-300 bg-white hover:bg-neutral-50"
          >
            <RefreshCw className={`mr-1.5 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#6E97A2]">Discovery</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Trend signals</h1>
          <p className="mt-1 max-w-2xl text-neutral-500">
            Find placement targets and read the room — viral artist &amp; playlist discovery (artist.tools),
            plus free public trend context. Fetched server-side and cached hourly. Real data only.
          </p>
        </div>

        <ViralDiscovery />
        <CreatorTrendsCard />

        <section className="mb-12">
          <div className="mb-4 flex items-center gap-2">
            <Search className="h-5 w-5 text-[#35606E]" />
            <h2 className="font-display text-xl font-bold">Trending searches &amp; topics</h2>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {(searchSources.length ? searchSources : [null]).map((s, i) =>
              s ? <SourceCard key={s.id} source={s} index={i} /> : <Skeleton key={i} className="h-96 rounded-2xl" />
            )}
          </div>
        </section>

        <section>
          <div className="mb-4 flex items-center gap-2">
            <Music2 className="h-5 w-5 text-[#35606E]" />
            <h2 className="font-display text-xl font-bold">Trending songs &amp; music videos</h2>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {(musicSources.length ? musicSources : [null, null]).map((s, i) =>
              s ? <SourceCard key={s.id} source={s} index={i} /> : <Skeleton key={i} className="h-96 rounded-2xl" />
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
